/**
 * Shared formatting + label helpers for the admin case-approvals surface.
 * Extracted from the former case-approvals.tsx monolith so the screen,
 * detail panel, and review modal share one copy.
 */
import type { PendingCase } from "../../../lib/caseApprovals";
import type { StatusPillTone } from "../../aspects/StatusPill";

export const TZ = "America/New_York";

export function formatRelative(iso: string | null): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const diff = Date.now() - t;
  const sec = Math.floor(diff / 1000);
  if (sec < 45) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: TZ,
  });
}

export function formatFullTimestamp(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const date = d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: TZ,
  });
  const time = d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: TZ,
    timeZoneName: "short",
  });
  return `${date} · ${time}`;
}

/** Friendly date-only label (no time) for the review modal. */
export function formatFriendlyDate(iso: string | null): string {
  if (!iso) return "Not set";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Not set";
  return d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: TZ,
  });
}

export function engagementLabel(e: PendingCase["engagement_model"]): string {
  if (e === "licensing") return "Licensing";
  if (e === "rm") return "Coravu RM";
  return "Hybrid";
}

/** Map a case status to a StatusPill tone. */
export function caseStatusTone(status: string): StatusPillTone {
  if (status === "approved" || status === "contract_signed" || status === "final_invoice_paid") {
    return "sage";
  }
  if (status === "denied" || status === "denied_terminal") return "grey";
  return "amber";
}
